import React, { useState } from 'react';
import { SimpleCameraCapture } from './SimpleCameraCapture';
import { DocExtractFromImage } from './DocExtractFromImage';

interface CameraCaptureOpenCVProps {
  onCapture: (file: File) => void;
  onCancel?: () => void;
  onScanComplete?: () => void;
}

export function CameraCaptureOpenCV({
  onCapture,
  onCancel,
  onScanComplete,
}: CameraCaptureOpenCVProps) {
  const [capturedImage, setCapturedImage] = useState<File | null>(null);
  const [showExtraction, setShowExtraction] = useState(false);
  const [scanCount, setScanCount] = useState(0);

  const handleSimpleCapture = (file: File) => {
    console.log('📸 [CAMERA] Image capturée, affichage extraction');
    setCapturedImage(file);
    setShowExtraction(true);
  };

  const handleExtracted = (file: File) => {
    console.log('✅ [EXTRACTION] Image extraite validée');
    setShowExtraction(false);
    setCapturedImage(null);
    setScanCount(prev => prev + 1);
    onCapture(file);
    
    // Ne pas fermer automatiquement, permettre de scanner d'autres documents
    // L'utilisateur doit explicitement fermer le scanner
  };

  const handleExtractionCancel = () => {
    console.log('❌ [EXTRACTION] Extraction annulée');
    setShowExtraction(false);
    setCapturedImage(null);
    // On retourne à la capture simple
  };

  const handleRetryCapture = () => {
    console.log('🔄 [CAMERA] Nouvelle capture demandée');
    setShowExtraction(false);
    setCapturedImage(null);
  };

  const handleCancel = () => {
    console.log('🛑 [CAMERA] Annulation complète');
    setShowExtraction(false);
    setCapturedImage(null);
    onCancel?.();
  };

  const handleFinishScanning = () => {
    console.log('🏁 [CAMERA] Fin du scan demandée');
    setShowExtraction(false);
    setCapturedImage(null);
    onScanComplete?.();
  };

  if (showExtraction && capturedImage) {
    return (
      <DocExtractFromImage
        originalImage={capturedImage}
        onExtracted={handleExtracted}
        onCancel={handleExtractionCancel}
        onRetryCapture={handleRetryCapture}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">
          Scanner de documents
        </h3>
        <div className="flex space-x-2">
          {scanCount > 0 && (
            <button
              type="button"
              onClick={handleFinishScanning}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Terminer ({scanCount} doc{scanCount > 1 ? 's' : ''})
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
        <p className="text-sm text-yellow-800 flex items-center">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Vous pouvez scanner plusieurs documents. Chaque capture sera ajoutée aux pièces jointes.
        </p>
      </div>

      <SimpleCameraCapture
        onCapture={handleSimpleCapture}
        onCancel={handleCancel}
      />
    </div>
  );
}